import { useEffect, useRef, useState } from 'react';
import { Sidebar } from './shell/Sidebar';
import { Topbar } from './shell/Topbar';
import { LoginScreen } from './shell/LoginScreen';
import { OverviewView } from './views/OverviewView';
import { ClientsView } from './views/ClientsView';
import { ClientView } from './views/ClientView';
import { PipelineView } from './views/PipelineView';
import { RevenueView } from './views/RevenueView';
import { SettingsView } from './views/SettingsView';
import { DocsView } from './views/DocsView';
import { PartnersView } from './views/PartnersView';
import { ExpensesView } from './views/ExpensesView';
import { LeadsView } from './views/LeadsView';
import { TemplatesView } from './views/TemplatesView';
import { OperativitaView } from './views/OperativitaView';
import { CassaforteView } from './views/CassaforteView';
import { NewClientModal } from './components/NewClientModal';
import { useClients, useDataPhase } from './data/store';

export type Route =
  | 'overview'
  | 'ops'
  | 'clients'
  | 'leads'
  | 'templates'
  | 'partners'
  | 'pipeline'
  | 'revenue'
  | 'expenses'
  | 'vault'
  | 'documenti'
  | 'settings';

const TITLES: Record<Route, { title: string; sub: string }> = {
  overview: { title: 'Panoramica', sub: 'Il tuo studio in un colpo d’occhio' },
  ops: { title: 'Operatività', sub: 'Cosa richiede attenzione adesso' },
  clients: { title: 'Clienti', sub: '' },
  leads: { title: 'Lead', sub: 'Trova e contatta nuovi clienti' },
  templates: { title: 'Modelli', sub: 'Messaggi pronti per WhatsApp ed email' },
  partners: { title: 'Partner', sub: 'Spazi di lavoro e provvigioni' },
  pipeline: { title: 'Pipeline', sub: 'Onboarding e lavori in corso' },
  revenue: { title: 'Ricavi', sub: 'Abbonamenti e fatturato' },
  expenses: { title: 'Spese', sub: 'Costi e margine dello studio' },
  vault: { title: 'Cassaforte', sub: 'Credenziali cifrate dei clienti' },
  documenti: { title: 'Documenti', sub: 'Guide e modelli dello studio' },
  settings: { title: 'Impostazioni', sub: 'Studio, piani e integrazioni' },
};

/** Top-level gate: login / loading / error / the Console itself. */
export function App() {
  const { phase, error, reload } = useDataPhase();
  if (phase === 'anon') return <LoginScreen />;
  if (phase === 'loading') return <Splash label="Carico la Console…" />;
  if (phase === 'error') return <Splash label={`Non riesco a caricare la Console.`} detail={error} onRetry={reload} />;
  return <Console />;
}

function Splash({ label, detail, onRetry }: { label: string; detail?: string; onRetry?: () => void }) {
  return (
    <div className="login-wrap">
      <div className="login-card" style={{ textAlign: 'center' }}>
        <div className="login-brand">
          Risma <em>studio</em> · Console
        </div>
        <p style={{ marginTop: 6 }}>{label}</p>
        {detail && <p className="rs-muted" style={{ fontSize: 12 }}>{detail}</p>}
        {onRetry && (
          <button type="button" className="btn btn-primary" style={{ marginTop: 16 }} onClick={onRetry}>
            Riprova
          </button>
        )}
      </div>
    </div>
  );
}

// ── browser-history-backed navigation ───────────────────────
const ROUTE_IDS: Route[] = ['overview', 'ops', 'clients', 'leads', 'templates', 'partners', 'pipeline', 'revenue', 'expenses', 'vault', 'documenti', 'settings'];
type Nav = { route: Route; selectedId: string | null };

function urlForNav(n: Nav): string {
  if (n.selectedId) return `/clients/${encodeURIComponent(n.selectedId)}`;
  return n.route === 'overview' ? '/' : `/${n.route}`;
}
function parseNav(path: string): Nav {
  const parts = path.replace(/^\/+|\/+$/g, '').split('/');
  if (parts[0] === 'clients' && parts[1]) return { route: 'clients', selectedId: decodeURIComponent(parts[1]) };
  const r = parts[0] as Route;
  return { route: ROUTE_IDS.includes(r) ? r : 'overview', selectedId: null };
}

function Console() {
  const [nav, setNav] = useState<Nav>(() => parseNav(window.location.pathname));
  const { route, selectedId } = nav;
  const [sideOpen, setSideOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const pushed = useRef(false);

  // Wire the browser back/forward buttons to in-app navigation.
  useEffect(() => {
    window.history.replaceState(nav, '', urlForNav(nav));
    const onPop = (e: PopStateEvent) => {
      const s = e.state as Nav | null;
      setNav(s && typeof s === 'object' && 'route' in s ? s : parseNav(window.location.pathname));
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const push = (next: Nav) => {
    window.history.pushState(next, '', urlForNav(next));
    pushed.current = true;
    setNav(next);
    setSideOpen(false);
  };
  const openClient = (id: string) => push({ route, selectedId: id });
  const go = (r: Route) => push({ route: r, selectedId: null });
  const back = () => {
    if (pushed.current) window.history.back();
    else push({ route: 'clients', selectedId: null });
  };

  const visible = useClients();
  // Resolve the open client from the workspace-scoped list, so switching to a
  // workspace that doesn't contain it cleanly falls back to the list (no stale
  // cross-workspace detail lingering).
  const selected = selectedId ? visible.find((c) => c.id === selectedId) ?? null : null;

  const head = selected
    ? { title: selected.name, sub: `${selected.category} · ${selected.locality}` }
    : route === 'clients'
      ? { title: 'Clienti', sub: `${visible.length} attività` }
      : TITLES[route];

  return (
    <div className="app">
      <Sidebar route={selected ? 'clients' : route} onNavigate={go} open={sideOpen} onClose={() => setSideOpen(false)} />
      <div className="main">
        <Topbar
          title={head.title}
          sub={head.sub}
          onMenu={() => setSideOpen(true)}
          onBack={selected ? back : undefined}
          onNewClient={() => setNewOpen(true)}
          onOpenClient={openClient}
        />
        {selected ? (
          <ClientView client={selected} />
        ) : route === 'overview' ? (
          <OverviewView onOpenClient={openClient} onGo={go} onNewClient={() => setNewOpen(true)} />
        ) : route === 'ops' ? (
          <OperativitaView onOpenClient={openClient} />
        ) : route === 'clients' ? (
          <ClientsView onOpenClient={openClient} />
        ) : route === 'leads' ? (
          <LeadsView />
        ) : route === 'templates' ? (
          <TemplatesView />
        ) : route === 'partners' ? (
          <PartnersView onOpenClient={openClient} onGo={go} />
        ) : route === 'pipeline' ? (
          <PipelineView onOpenClient={openClient} />
        ) : route === 'revenue' ? (
          <RevenueView onOpenClient={openClient} />
        ) : route === 'expenses' ? (
          <ExpensesView />
        ) : route === 'vault' ? (
          <CassaforteView />
        ) : route === 'documenti' ? (
          <DocsView />
        ) : (
          <SettingsView />
        )}
      </div>
      {newOpen && <NewClientModal onClose={() => setNewOpen(false)} />}
    </div>
  );
}
